/**
 * @file Auth — Google sign-in. Every verified @strv.com account passes;
 * what they can actually read is decided per-render by `isAdmin()` and
 * server-side by firestore.rules.
 *
 * Sign-in flow:
 *   1. signInWithPopup(googleProvider) — `prompt: select_account` always
 *      shows the picker so engineers with multiple Google accounts pick
 *      the right one. (Redirect on phones; a custom token when the IT Hub
 *      signs the person in — see signInFromHub.)
 *   2. Reject non-@strv.com immediately (signOut + throw).
 *   3. onAuthStateChanged drives the rest of the app: signed-in @strv.com
 *      lands on the role-appropriate home; otherwise the login screen.
 */

import {
  signInWithPopup, signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, signInWithCustomToken,
  signOut, onAuthStateChanged,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

import { auth, googleProvider, db } from './firebase.js';
import { STRV_DOMAIN_RE } from './config.js';
import { logAuditEvent }     from './audit-log.js';
import { AUDIT_ACTION }      from './lib/audit-actions.js';
import { clearSearchIndex }  from './search.js';
import { clearEmployeePickerCache } from './employee-picker.js';
import { inFrame } from './standard/js/bridge.js';
import { confirmDialog } from './standard/js/dialog.js';
import { setSessionOrigin, sessionOrigin } from './lib/session-origin.js';

// Session-storage key used to gate the AUTH_SIGNIN audit emit to once per
// browser tab. Without this, every page reload would write a new sign-in
// row and drown the audit log.
const SIGNIN_SESSION_KEY = 'gear.audit.signin_logged';

let currentUser = null;
const listeners = new Set();

// Live admin status. /admins/{lowercasedEmail} either exists (admin) or
// doesn't. The Firestore rule permits ONLY admins to read /admins, so
// the listener behaves differently for the two roles:
//   • Admin: snapshot fires with exists() → iAmAdmin = true. Demotion
//     (another admin removes them) flips the rule to permission-denied,
//     the error callback lands us at iAmAdmin = false within ~1s.
//   • Non-admin: snapshot errors immediately with permission-denied.
//     iAmAdmin stays false. Newly-promoted users will see the admin UI
//     after a refresh — no real-time detection of promotion (by design,
//     to keep the read surface tight).
//
// `adminStatusKnown` flips to true as soon as EITHER the success or
// error callback fires for the first time. Until then, `isAdmin()`
// reads `false` (the default) — which would cause an admin who hits
// /devices/:id directly (e.g. on refresh) to get redirected to /me
// before we've confirmed they're an admin. The renderer uses
// `adminStatusReady()` to defer that role gate during the few ms it
// takes the listener to fire.
let iAmAdmin = false;
let adminStatusKnown = false;
let unsubAdminDoc = null;
function notifyAdminListeners() {
  for (const cb of listeners) {
    try { cb(currentUser); } catch (err) { console.error('[auth] admin-toggle listener error', err); }
  }
}
function subscribeToAdminStatus(email) {
  if (unsubAdminDoc) {
    try { unsubAdminDoc(); } catch { /* ignore */ }
    unsubAdminDoc = null;
  }
  if (!email) {
    iAmAdmin = false;
    adminStatusKnown = false;
    return;
  }
  // While the listener spins up, admin status is unknown — `iAmAdmin`
  // stays whatever it was. The renderer waits on `adminStatusKnown`
  // before acting on the answer.
  adminStatusKnown = false;
  unsubAdminDoc = onSnapshot(
    doc(db, 'admins', String(email).toLowerCase()),
    (snap) => {
      const was = iAmAdmin;
      const wasKnown = adminStatusKnown;
      iAmAdmin = snap.exists();
      adminStatusKnown = true;
      if (was !== iAmAdmin || !wasKnown) notifyAdminListeners();
    },
    (err) => {
      // permission-denied is the EXPECTED outcome for non-admins (the
      // rule denies them) — no console noise. Other codes (rule typo,
      // network) are worth a warn.
      const was = iAmAdmin;
      const wasKnown = adminStatusKnown;
      iAmAdmin = false;
      adminStatusKnown = true;
      if (err?.code !== 'permission-denied') {
        console.warn('[auth] admin-doc watch failed', err?.code || err?.message || err);
      }
      if (was !== iAmAdmin || !wasKnown) notifyAdminListeners();
    },
  );
}

/** Subscribe to auth state changes. Returns the unsubscribe fn. */
export function onAuth(cb) {
  listeners.add(cb);
  cb(currentUser);
  return () => listeners.delete(cb);
}

export function getCurrentUser() {
  return currentUser;
}

/**
 * True iff the current signed-in user is in /admins. Backed by the live
 * Firestore listener wired up below; flips within ~1s of an admin being
 * added or removed (no refresh needed).
 */
export function isAdmin() {
  return currentUser != null && iAmAdmin;
}

/**
 * True once the admin-status listener has produced its first result for
 * the current user (admin or not). Callers that gate UI on `isAdmin()`
 * should defer that decision until this returns true, otherwise an admin
 * refreshing on /devices/:id gets bounced to /me during the ~50ms before
 * the listener fires.
 */
export function adminStatusReady() {
  return currentUser == null || adminStatusKnown;
}

/** True for any verified @strv.com sign-in (i.e. anyone who can see /me). */
export function isStrvUser() {
  if (!currentUser?.email) return false;
  return STRV_DOMAIN_RE.test(currentUser.email);
}

/**
 * Phones get the redirect flow — popups are unreliable on iOS Safari and
 * impossible from a home-screen app — but only once the auth domain IS this
 * host: Safari's storage partitioning breaks a redirect that bounces through
 * firebaseapp.com. Production uses gear.strv.io; any other host (localhost,
 * firebaseapp.com) falls back to the popup.
 */
function useRedirectSignIn() {
  // Inside the IT Hub's frame a redirect would take the frame itself to
  // Google, which refuses to be framed — the popup works there.
  if (typeof matchMedia !== 'function' || inFrame()) return false;
  const sameHost   = auth.config?.authDomain === location.hostname;
  const touch      = matchMedia('(pointer: coarse)').matches;
  const standalone = matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  return sameHost && (touch || standalone);
}

export async function signInWithGoogle() {
  if (useRedirectSignIn()) {
    await signInWithRedirect(auth, googleProvider); // page leaves; onAuthStateChanged picks up the return
    return null;
  }
  const result = await signInWithPopup(auth, googleProvider);
  const email = result.user?.email || '';
  if (!STRV_DOMAIN_RE.test(email)) {
    await signOut(auth);
    throw new Error(`That account isn't on the STRV domain.`);
  }
  setSessionOrigin('self');
  return result.user;
}

export async function signOutCurrent() {
  setSessionOrigin(null);
  await signOut(auth);
}

// ── Sessions started by the IT Hub (playbook §6) ──────────────────────────
// Inside the hub's frame the hub hands Gear a 5-minute sign-in proof; the
// backend (POST /api/hub/session) turns it into a Firebase session for the
// SAME user as the person's own Google sign-in. Who started the session is
// remembered, because the hub may only end the sessions it started.

/** The proof's claims, read (not verified — the backend does that) only to compare accounts. */
function proofEmail(token) {
  try {
    const json = atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'));
    return String(JSON.parse(json).email || '').toLowerCase();
  } catch { return ''; }
}

let hubSignIn = null;

/** Hub → `session { token }`. The hub sends one per handshake; overlapping ones collapse. */
export function signInFromHub(token) {
  hubSignIn ||= exchangeHubToken(token).finally(() => { hubSignIn = null; });
  return hubSignIn;
}

async function exchangeHubToken(token) {
  await auth.authStateReady();
  const hubEmail = proofEmail(token);
  const here = (auth.currentUser?.email || '').toLowerCase();
  if (here && here === hubEmail) return; // already that person
  if (here) {
    const switchTo = await confirmDialog({
      title: `Use ${hubEmail} in Gear?`,
      body: `Gear is signed in as ${here}.`,
      confirm: `Use ${hubEmail}`,
      cancel: `Keep ${here}`,
    });
    if (!switchTo) return;
  }
  const before = sessionOrigin();
  try {
    const res = await fetch('/api/hub/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.customToken) throw new Error(body.error || `HTTP ${res.status}`);
    // Set before signing in: the sign-in fires the auth.signin audit entry,
    // which must already say via: 'hub'.
    setSessionOrigin('hub');
    await signInWithCustomToken(auth, body.customToken);
  } catch (err) {
    setSessionOrigin(before);
    // The login screen stays; the person can still sign in with Google.
    console.warn('[auth] sign-in from the IT Hub failed', err?.message || err);
  }
}

/** Hub → `signout { scope }`: 'hub' ends only sessions the hub started; 'everywhere' ends any. */
export async function signOutIfHubStarted(scope) {
  if (!auth.currentUser) return;
  if (scope === 'everywhere' || sessionOrigin() === 'hub') {
    clearSearchIndex();
    await signOutCurrent();
  }
}

/**
 * Emulator-only sign-in for the headless screenshot harness: an email /
 * password user the seed script creates in the Auth emulator. Refuses to
 * run against real Firebase — the emulator flag is inlined at build time,
 * so in production this is an unreachable throw and the import is dropped.
 */
export async function emulatorSignIn(email) {
  if (import.meta.env.VITE_USE_EMULATORS !== 'true') throw new Error('emulator sign-in is emulator-only');
  if (!STRV_DOMAIN_RE.test(email)) throw new Error('harness users must be @strv.com');
  const result = await signInWithEmailAndPassword(auth, email, 'gear-emulator');
  return result.user;
}

// Redirect flow: surface a failed return (wrong domain, cancelled consent)
// instead of silently landing back on the login page.
getRedirectResult(auth)
  .then((result) => { if (result?.user) setSessionOrigin('self'); })
  .catch((err) => console.error('[auth] redirect sign-in failed', err?.code || err));

onAuthStateChanged(auth, (user) => {
  // Defensive: if Firebase restores a session for a non-@strv account
  // (e.g. workspace policy changed), force them out before the rest of
  // the app picks up the user.
  if (user && !STRV_DOMAIN_RE.test(user.email || '')) {
    signOut(auth).catch(() => {});
    return;
  }
  currentUser = user;
  // Drop every in-memory data cache the moment there's no user. These hold
  // admin-only PII and financials (homeEmail, termination dates, purchase
  // prices), and a session that simply EXPIRES never passes through the
  // Sign-out menu item that used to be the only clear path — so they'd
  // otherwise survive in the tab for whoever signs in next.
  if (!user) {
    clearSearchIndex();
    clearEmployeePickerCache();
  }
  // (Re)subscribe to /admins/{email}. When the user signs out, this
  // unsubscribes; when they sign in, we start watching their own row.
  subscribeToAdminStatus(user?.email || null);
  // Audit-log the sign-in. Gated by sessionStorage so refreshes don't spam
  // — one row per browser tab per sign-in. We pass an explicit actor
  // because the audit emitter reads from auth.currentUser, which may race
  // this very callback on cold-start.
  if (user && typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(SIGNIN_SESSION_KEY)) {
    sessionStorage.setItem(SIGNIN_SESSION_KEY, '1');
    logAuditEvent({
      action: AUDIT_ACTION.AUTH_SIGNIN,
      actor:  { type: 'user', uid: user.uid, email: user.email, name: user.displayName || null, ...(sessionOrigin() === 'hub' ? { via: 'hub' } : {}) },
    });
  }
  for (const cb of listeners) {
    try { cb(user); } catch (err) { console.error('[auth] listener error', err); }
  }
});
